import { lazy, Suspense } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { AppRoutes, getRouteClubManager, getRouteCommunityClub, getRouteMain } from "@/shared/config/router";
import { AppRoutesProps } from "@/shared/types/router";
import { LeagueUsersRoute } from "../ui/LeagueUsersRoute";
import { ProductsRoute } from "../ui/ProductsRoute"; // v5-products
import { MyGiftsRoute } from "../ui/MyGiftsRoute";
import { AchievementsRoute } from "../ui/AchievementsRoute";
import { SiteWorkoutPageGate } from "../ui/SiteWorkoutPageGate";
import { StreamsRoute } from "../ui/StreamsRoute";

/**
 * Старый плоский адрес → тот же экран внутри кабинета.
 *
 * Страницы, которые открываются из бокового меню кабинета, должны рисоваться
 * вместе с ним. Перенос меняет адрес, а адреса уже разошлись по ссылкам и
 * закладкам, поэтому старый путь не удаляется, а ведёт на новый.
 */
// Короткий адрес встречи → её страница в кабинете (id сохраняется).
const ToAccountActivity: React.FC = () => {
    const { lang, id } = useParams<{ lang: string; id: string }>();
    const { search, hash } = useLocation();
    return <Navigate to={`/${lang || "ru"}/account/activity/${id}${search}${hash}`} replace />;
};

const ToAccount = ({ to }: { to: string }) => {
    const { lang } = useParams<{ lang: string }>();
    const { search, hash } = useLocation();
    return <Navigate to={`/${lang}/account/${to}${search}${hash}`} replace />;
};

// Календарь встреч слился с календарём тренировок. Старый адрес остаётся,
// чтобы ссылки, туры и глубокие ссылки приложения не умерли, а ?d=, ?v= и
// ?id= доезжают как есть — общий календарь понимает их так же.
const ToWorkoutsCalendar = () => <ToAccount to="workouts" />;

// Уведомление руководителю о заявке в клуб приходит из воркера с адресом
// /account/club?tab=requests&club=… — такого маршрута на сайте нет, страница
// клуба живёт в «Общении». Без этого редиректа нажатие на уведомление
// приводило на 404. Строка запроса доезжает как есть.
const ToCommunityClub = () => {
    const { lang } = useParams<{ lang: string }>();
    const { search, hash } = useLocation();
    return <Navigate to={`${getRouteCommunityClub(lang || "ru")}${search}${hash}`} replace />;
};

// Кабинет клуба (03.11): /account/club?tab=requests&club=X (ссылка старых уведомлений о
// заявке) ведёт прямо в заявки кабинета клуба club-manager/requests?club=X, прочее — на
// страницу клуба, как раньше. Ворот на .com нет: не руководителю кабинет сам покажет
// «Я веду этот клуб».
const ToClubCabinet = () => {
    const { lang } = useParams<{ lang: string }>();
    const { search } = useLocation();
    const qs = new URLSearchParams(search);
    const club = qs.get("club");
    if (qs.get("tab") === "requests" && club) {
        return <Navigate to={`${getRouteClubManager(lang || "ru")}/requests?club=${encodeURIComponent(club)}`} replace />;
    }
    return <ToCommunityClub />;
};

// Старые страницы скрыты (владелец, 09.10.2026): /:lang/main — прежний
// лендинг MainPageOld с кнопками «PAY $…» и записью через Google Form,
// /:lang/mainru — Main1Page, диагностика за 27 350 ₽ через
// consultation.torsunov.ru. Ссылок на них нет, а продавать мимо нынешних
// правил они не должны (диету не заказывают, диагностика ЗОЖ — только на
// ogtorsunov.ru). Адрес открывает главную того же языка — что покажет /:lang,
// решает LangIndexRoute (AppRouter.tsx), — с заменой записи истории и без
// входа; ?ref= и метки доезжают. Сами страницы лежат в src/pages, маршрута к
// ним нет.
const ToLangHome = () => {
    const { lang } = useParams<{ lang: string }>();
    const { search, hash } = useLocation();
    return <Navigate to={`${getRouteMain(lang)}${search}${hash}`} replace />;
};

const CourseCutsPage = lazy(() =>
    import("@/pages/CourseCutsPage").then((m) => ({ default: m.CourseCutsPage })),
);
const ClipCutsPage = lazy(() =>
    import("@/pages/ClipCutsPage").then((m) => ({ default: m.ClipCutsPage })),
);
const CutPreviewPage = lazy(() =>
    import("@/pages/CutPreviewPage").then((m) => ({ default: m.CutPreviewPage })),
);

const LecturesPage = lazy(() =>
    import("@/pages/LecturesPage").then((m) => ({ default: m.LecturesPage })),
);
const TranslatedLecturesPage = lazy(() =>
    import("@/pages/TranslatedLecturesPage").then((m) => ({ default: m.TranslatedLecturesPage })),
);

// Lazy: pulls livekit-client, which must not sit in the main bundle.
const AdminStreamPage = lazy(() =>
    import("@/pages/AdminStreamPage").then((m) => ({ default: m.AdminStreamPage })),
);
const DeviceCheckPage = lazy(() =>
    import("@/pages/DeviceCheckPage").then((m) => ({ default: m.DeviceCheckPage })),
);
const LiveCallPage = lazy(() =>
    import("@/pages/LiveCallPage").then((m) => ({ default: m.LiveCallPage })),
);
const AdminLiveStagePage = lazy(() =>
    import("@/pages/AdminLiveStagePage/ui/AdminLiveStagePage").then((m) => ({
        default: m.AdminLiveStagePage,
    })),
);
const AdminShowPage = lazy(() =>
    import("@/pages/AdminShowPage").then((m) => ({ default: m.AdminShowPage })),
);

const QuestionsPage = lazy(() =>
    import("@/pages/QuestionsPage").then((m) => ({ default: m.QuestionsPage })),
);

const TestingWorkoutPage = lazy(() =>
    import("@/pages/TestingWorkoutPage").then((m) => ({ default: m.TestingWorkoutPage })),
);
const AnswersPage = lazy(() =>
    import("@/pages/AnswersPage").then((m) => ({ default: m.AnswersPage })),
);

const WatchLinkPage = lazy(() =>
    import("@/pages/WatchLinkPage").then((m) => ({ default: m.WatchLinkPage })),
);
const EditorLinkPage = lazy(() =>
    import("@/pages/EditorLinkPage").then((m) => ({ default: m.EditorLinkPage })),
);

// Lazy load heavy pages (video.js, @heygen, MUI charts, etc.)
const AccountPage = lazy(() =>
    import("@/pages/Account").then((m) => ({ default: m.default })),
);
// Не через index папки: он отдаёт и скрытый MainPageOld (см. ToLangHome), и
// тот уезжал бы в сборку вместе с заглушкой /landing.
const MainPage = lazy(() =>
    import("@/pages/MainPage/ui/MainPage").then((m) => ({ default: m.MainPage })),
);
const DietPage = lazy(() =>
    import("@/pages/Diet").then((m) => ({ default: m.DietPage })),
);
const TreesPage = lazy(() =>
    import("@/pages/Trees").then((m) => ({ default: m.TreesPage })),
);
const ProtectedContent = lazy(() =>
    import("@/pages/ProtectedContentPage").then((m) => ({
        default: m.ProtectedContent,
    })),
);
const MyReviewPage = lazy(() =>
    import("@/pages/MyRevivePage").then((m) => ({ default: m.MyRevivePage })),
);

const HealthGuideNewPage = lazy(() =>
    import("@/pages/HealthGuideNewPage").then((m) => ({
        default: m.HealthGuideNewPage,
    })),
);
const VideoCoursePage = lazy(() =>
    import("@/pages/VideoCoursePage").then((m) => ({
        default: m.VideoCoursePage,
    })),
);

const Diagnose = lazy(() =>
    import("@/pages/DiagnoseDiagram").then((m) => ({
        default: m.DiagnoseDiagram,
    })),
);

const DiagnosticQuestionnairePage = lazy(() =>
    import("@/pages/DiagnosticQuestionnairePage").then((m) => ({
        default: m.DiagnosticQuestionnairePage,
    })),
);

const OrderAfterPayPage = lazy(() =>
    import("@/pages/OrderAfterPayPage").then((m) => ({
        default: m.OrderAfterPayPage,
    })),
);

const PaymentSuccessPage = lazy(() =>
    import("@/pages/PaymentSuccessPage").then((m) => ({
        default: m.PaymentSuccessPage,
    })),
);

const ReviewsSection = lazy(() =>
    import("@/pages/Account/ui/ReviewsSection/ReviewsSection").then((m) => ({
        default: m.ReviewsSection,
    })),
);
const SpecialistChatPage = lazy(() =>
    import("@/pages/Account/ui/SpecialistChatPage/SpecialistChatPage").then((m) => ({ default: m.SpecialistChatPage })),
);
const KnowledgePage = lazy(() =>
    import("@/pages/Account/ui/SpecialistChatPage/ui/KnowledgePage/KnowledgePage").then((m) => ({ default: m.KnowledgePage })),
);
const TelegramAccessPage = lazy(() =>
    import("@/pages/TelegramAccessPage").then((m) => ({ default: m.TelegramAccessPage })),
);

const AccountActivitiesMap = lazy(() =>
    import("@/pages/AccountActivitiesMap").then((m) => ({
        default: m.AccountActivitiesMap,
    })),
);

const ActivityPage = lazy(() =>
    import("@/pages/ActivityPage").then((m) => ({
        default: m.ActivityPage,
    })),
);

const AdminGeoDensityPage = lazy(() =>
    import("@/pages/AdminGeoDensityPage").then((m) => ({
        default: m.AdminGeoDensityPage,
    })),
);

const GoodDeedsPage = lazy(() =>
    import("@/pages/GoodDeedsPage").then((m) => ({
        default: m.GoodDeedsPage,
    })),
);

const AdminQuestionDatabasePage = lazy(() =>
    import("@/pages/AdminQuestionDatabasePage").then((m) => ({
        default: m.AdminQuestionDatabasePage,
    })),
);
const AdminUserQuestionsPage = lazy(() =>
    import("@/pages/AdminUserQuestionsPage").then((m) => ({
        default: m.AdminUserQuestionsPage,
    })),
);
const AdminUserQuestionDetailPage = lazy(() =>
    import("@/pages/AdminUserQuestionDetailPage").then((m) => ({
        default: m.AdminUserQuestionDetailPage,
    })),
);
const AdminRejectReasonsPage = lazy(() =>
    import("@/pages/AdminRejectReasonsPage").then((m) => ({
        default: m.AdminRejectReasonsPage,
    })),
);

const AdminAnswerCorrectionsPage = lazy(() =>
    import("@/pages/AdminAnswerCorrectionsPage").then((m) => ({
        default: m.AdminAnswerCorrectionsPage,
    })),
);
const AdminAnswerCorrectionEditorPage = lazy(() =>
    import("@/pages/AdminAnswerCorrectionEditorPage").then((m) => ({
        default: m.AdminAnswerCorrectionEditorPage,
    })),
);
const AdminCorrectionsPage = lazy(() =>
    import("@/pages/AdminCorrectionsPage").then((m) => ({
        default: m.AdminCorrectionsPage,
    })),
);
const AdminCommunityPage = lazy(() =>
    import("@/pages/AdminCommunityPage").then((m) => ({
        default: m.AdminCommunityPage,
    })),
);
const AdminModerationPage = lazy(() =>
    import("@/pages/AdminModerationPage").then((m) => ({
        default: m.AdminModerationPage,
    })),
);
const AdminCourseQaPage = lazy(
    () => import("@/pages/AdminCourseQaPage/ui/AdminCourseQaPage"),
);
const AdminErrorReportsPage = lazy(
    () => import("@/pages/AdminErrorReportsPage/ui/AdminErrorReportsPage"),
);
const AdminCrashReportsPage = lazy(() =>
    import("@/pages/AdminCrashReportsPage").then((m) => ({
        default: m.AdminCrashReportsPage,
    })),
);
const AdminChangelogPage = lazy(() =>
    import("@/pages/AdminChangelogPage").then((m) => ({
        default: m.AdminChangelogPage,
    })),
);
const AdminGlossaryPage = lazy(() =>
    import("@/pages/AdminGlossaryPage").then((m) => ({
        default: m.AdminGlossaryPage,
    })),
);

const MyCoursesPage = lazy(() =>
    import("@/pages/MyCoursesPage").then((m) => ({
        default: m.MyCoursesPage,
    })),
);

const MyCoursesPageNew = lazy(() =>
    import("@/pages/MyCoursesPageNew").then((m) => ({
        default: m.MyCoursesPageNew,
    })),
);

const MyCoursesGroupPage = lazy(() =>
    import("@/pages/MyCoursesGroupPage").then((m) => ({
        default: m.MyCoursesGroupPage,
    })),
);

const MyCoursesCoursePage = lazy(() =>
    import("@/pages/MyCoursesCoursePage").then((m) => ({
        default: m.MyCoursesCoursePage,
    })),
);

const MyCoursesLessonPage = lazy(() =>
    import("@/pages/MyCoursesLessonPage").then((m) => ({
        default: m.MyCoursesLessonPage,
    })),
);

const BuyCoursePage = lazy(() =>
    import("@/pages/BuyCoursePage").then((m) => ({ default: m.BuyCoursePage })),
);
const BuySubscriptionPage = lazy(() =>
    import("@/pages/BuySubscriptionPage").then((m) => ({
        default: m.BuySubscriptionPage,
    })),
);
const ManualHubPage = lazy(() =>
    import("@/pages/ManualPage").then((m) => ({ default: m.ManualHubPage })),
);
const ManualPage = lazy(() =>
    import("@/pages/ManualPage").then((m) => ({ default: m.ManualPage })),
);

const AffiliatePage = lazy(() =>
    import("@/pages/AffiliatePage").then((m) => ({ default: m.AffiliatePage })),
);

const PackagesPage = lazy(() =>
    import("@/pages/PackagesPage").then((m) => ({ default: m.PackagesPage })),
);

const GiftClaimPage = lazy(() =>
    import("@/pages/GiftClaimPage").then((m) => ({ default: m.GiftClaimPage })),
);

const AdminCoursePaymentsPage = lazy(() =>
    import("@/pages/AdminCoursePaymentsPage").then((m) => ({
        default: m.AdminCoursePaymentsPage,
    })),
);

const AdminCouponDistributionPage = lazy(() =>
    import("@/pages/AdminCouponDistributionPage").then((m) => ({
        default: m.AdminCouponDistributionPage,
    })),
);

const AdminSubscriptionsPage = lazy(() =>
    import("@/pages/AdminSubscriptionsPage").then((m) => ({
        default: m.AdminSubscriptionsPage,
    })),
);

const MethodPage = lazy(() =>
    import("@/pages/MethodPage").then((m) => ({
        default: m.MethodPage,
    })),
);

const GetAppPage = lazy(() =>
    import("@/pages/GetAppPage").then((m) => ({
        default: m.GetAppPage,
    })),
);

const AdminGooglePlayPromosPage = lazy(() =>
    import("@/pages/AdminGooglePlayPromosPage").then((m) => ({
        default: m.AdminGooglePlayPromosPage,
    })),
);

const AdminTestingPage = lazy(() =>
    import("@/pages/AdminTestingPage").then((m) => ({
        default: m.AdminTestingPage,
    })),
);

const AdminPersonalConsultationPage = lazy(() =>
    import("@/pages/AdminPersonalConsultationPage").then((m) => ({
        default: m.AdminPersonalConsultationPage,
    })),
);

const NotificationSettingsPage = lazy(() =>
    import(
        "@/pages/Account/ui/NotificationSettingsPage/NotificationSettingsPage"
    ).then((m) => ({
        default: m.NotificationSettingsPage,
    })),
);

const ProfilePage = lazy(() =>
    import("@/pages/Account/ui/ProfilePage/ProfilePage").then((m) => ({
        default: m.ProfilePage,
    })),
);

const NotificationsPage = lazy(() =>
    import("@/pages/Account/ui/NotificationsPage/NotificationsPage").then(
        (m) => ({
            default: m.NotificationsPage,
        }),
    ),
);

const WorkoutsPage = lazy(() =>
    import("@/pages/WorkoutsPage/ui/WorkoutsPage").then((m) => ({
        default: m.WorkoutsPage,
    })),
);
const AnalyticsPage = lazy(() =>
    import("@/pages/WorkoutsPage/ui/AnalyticsPage/AnalyticsPage").then((m) => ({
        default: m.AnalyticsPage,
    })),
);
// «История баллов» тренировок (недели → неделя → почему столько баллов).
const PointsHistoryPage = lazy(() =>
    import("@/pages/PointsHistoryPage").then((m) => ({
        default: m.PointsHistoryPage,
    })),
);
const TrainingBasisPage = lazy(() =>
    import("@/pages/TrainingBasisPage").then((m) => ({
        default: m.TrainingBasisPage,
    })),
);
const ConsultationsSection = lazy(() =>
    import("@/pages/Account/ui/ConsultationsSection/ConsultationsSection").then(
        (m) => ({
            default: m.ConsultationsSection,
        }),
    ),
);
const HomeSection = lazy(() =>
    import("@/pages/Account/ui/HomeSection/HomeSection").then((m) => ({
        default: m.HomeSection,
    })),
);
const ConsultationsProfileSection = lazy(() =>
    import("@/pages/Account/ui/ConsultationsProfileSection/ConsultationsProfileSection").then(
        (m) => ({
            default: m.ConsultationsProfileSection,
        }),
    ),
);

const IndiaVideoAnswerPage = lazy(() =>
    import("@/pages/IndiaVideoAnswerPage").then((m) => ({
        default: m.IndiaVideoAnswerPage,
    })),
);
const AdminDirectAnswerPage = lazy(() =>
    import("@/pages/AdminDirectAnswerPage").then((m) => ({
        default: m.AdminDirectAnswerPage,
    })),
);
const AdminClipInsertsPage = lazy(() =>
    import("@/pages/AdminClipInsertsPage").then((m) => ({
        default: m.AdminClipInsertsPage,
    })),
);
const LoginPage = lazy(() =>
    import("@/pages/LoginPage").then((m) => ({
        default: m.LoginPage,
    })),
);
const GoogleCallbackPage = lazy(() =>
    import("@/pages/GoogleCallbackPage").then((m) => ({
        default: m.GoogleCallbackPage,
    })),
);
const CalcPage = lazy(() =>
    import("@/pages/CalculatorPage").then((m) => ({
        default: m.CalcPage,
    })),
);
const TermsAndConditionsPage = lazy(() =>
    import("@/pages/TermsAndConditionsPage").then((m) => ({
        default: m.TermsAndConditionsPage,
    })),
);
const PrivacyPolicyPage = lazy(() =>
    import("@/pages/PrivacyPolicyPage").then((m) => ({
        default: m.PrivacyPolicyPage,
    })),
);
const FitUploadPage = lazy(() =>
    import("@/pages/FitUploadPage").then((m) => ({
        default: m.FitUploadPage,
    })),
);
const CoursePage = lazy(() =>
    import("@/pages/CoursePage/ui").then((m) => ({
        default: m.CoursePage,
    })),
);
const BucketFilesPage = lazy(() =>
    import("@/pages/BucketFilesPage").then((m) => ({
        default: m.BucketFilesPage,
    })),
);
const YouTubeVideosPage = lazy(() =>
    import("@/pages/YouTubeVideosPage").then((m) => ({
        default: m.YouTubeVideosPage,
    })),
);
const WorkoutReviewPage = lazy(() =>
    import("@/pages/WorkoutReviewPage").then((m) => ({
        default: m.WorkoutReviewPage,
    })),
);
const ActivityReviewPage = lazy(() =>
    import("@/pages/ActivityReviewPage").then((m) => ({
        default: m.ActivityReviewPage,
    })),
);
const ClubManagerPage = lazy(() =>
    import("@/pages/ClubManagerPage").then((m) => ({
        default: m.ClubManagerPage,
    })),
);
// Кабинет клуба (03.11): страница ссылки-приглашения и админская страница клубов.
const ClubJoinPage = lazy(() =>
    import("@/pages/ClubJoinPage").then((m) => ({
        default: m.ClubJoinPage,
    })),
);
const AdminClubsPage = lazy(() =>
    import("@/pages/AdminClubsPage").then((m) => ({
        default: m.AdminClubsPage,
    })),
);
const AdminMarathonsPage = lazy(() =>
    import("@/pages/AdminMarathonsPage").then((m) => ({
        default: m.AdminMarathonsPage,
    })),
);
const AdminLeagueMarathonsPage = lazy(() =>
    import("@/pages/AdminLeagueMarathonsPage").then((m) => ({
        default: m.AdminLeagueMarathonsPage,
    })),
);
const PlanCreatePage = lazy(() =>
    import("@/pages/PlanCreatePage").then((m) => ({
        default: m.PlanCreatePage,
    })),
);
const AdminClubsRadiusPage = lazy(() =>
    import("@/pages/AdminClubsRadiusPage").then((m) => ({
        default: m.AdminClubsRadiusPage,
    })),
);
const LeaguePage = lazy(() =>
    import("@/pages/LeaguePage").then((m) => ({
        default: m.LeaguePage,
    })),
);
const AnswerPage = lazy(() =>
    import("@/pages/AnswerPage").then((m) => ({
        default: m.AnswerPage,
    })),
);
const ActivitiesMapPage = lazy(() =>
    import("@/pages/ActivitiesMapPage").then((m) => ({
        default: m.ActivitiesMapPage,
    })),
);
const Player = lazy(() =>
    import("@/widgets/Player").then((m) => ({
        default: m.Player,
    })),
);
const AreaOfLifePage = lazy(() =>
    import("@/pages/AreaOfLifePage").then((m) => ({
        default: m.AreaOfLifePage,
    })),
);
const ScoringAnalysisPage = lazy(() =>
    import("@/pages/ScoringAnalysisPage").then((m) => ({
        default: m.ScoringAnalysisPage,
    })),
);
const AdminDashboardPage = lazy(() =>
    import("@/pages/AdminDashboardPage").then((m) => ({
        default: m.AdminDashboardPage,
    })),
);
const AdminUserProfilePage = lazy(() =>
    import("@/pages/AdminUserProfilePage").then((m) => ({
        default: m.AdminUserProfilePage,
    })),
);
const PrivacyPolicyPublicPage = lazy(() =>
    import("@/pages/PrivacyPolicyPublicPage").then((m) => ({ default: m.PrivacyPolicyPublicPage })),
);

// Play requires account deletion to be reachable from a public URL, not only from
// inside the app: someone who uninstalled or cannot sign in must still be able to ask.
const DeleteAccountPage = lazy(() =>
    import("@/pages/DeleteAccountPage").then((m) => ({ default: m.DeleteAccountPage })),
);
const CancellationPolicyPage = lazy(() =>
    import("@/pages/CancellationPolicyPage").then((m) => ({ default: m.CancellationPolicyPage })),
);

const OgtMainPage = lazy(() =>
    import("@/pages/OgtMainPage").then((m) => ({ default: m.OgtMainPage })),
);
const AboutOgtPage = lazy(() =>
    import("@/pages/AboutOgtPage").then((m) => ({ default: m.AboutOgtPage })),
);
const VerifyEmailPage = lazy(() =>
    import("@/pages/VerifyEmailPage").then((m) => ({
        default: m.VerifyEmailPage,
    })),
);
const ConfirmPurchasePage = lazy(() =>
    import("@/pages/ConfirmPurchasePage").then((m) => ({
        default: m.ConfirmPurchasePage,
    })),
);
const SetPasswordPage = lazy(() =>
    import("@/pages/SetPasswordPage").then((m) => ({
        default: m.SetPasswordPage,
    })),
);
const PublicQuestionsPage = lazy(() =>
    import("@/pages/PublicQuestionsPage").then((m) => ({
        default: m.PublicQuestionsPage,
    })),
);
const CutVideosPage = lazy(() =>
    import("@/pages/CutVideosPage").then((m) => ({
        default: m.CutVideosPage,
    })),
);
const CommunityPage = lazy(() =>
    import("@/pages/CommunityPage").then((m) => ({
        default: m.CommunityPage,
    })),
);
// /account/community: админу — новая страница «Общение и встречи» (V5),
// всем остальным — прежняя CommunityMapPage без изменений (гейт внутри).
const CommunityHomeGate = lazy(() =>
    import("@/pages/CommunityHubV5").then((m) => ({
        default: m.CommunityHomeGate,
    })),
);
// «Тренировки»: у человека без приложения на сайте там только карточка
// приложения и тестовая тренировка. С рубильником SITE_WORKOUT_PAGES_HIDDEN =
// "all" — всем (прежнее меню ведёт сюда); иначе — предпросмотр для админов, а
// остальным адрес отвечает тем же голым «404», что и до его появления
// (калитки — RequireAuth для невошедших и AccountPage, pages/Account/index.tsx,
// ещё до кабинета).
const TrainingAppPage = lazy(() =>
    import("@/pages/AppOnlySectionPage").then((m) => ({
        default: m.TrainingAppPage,
    })),
);
// «Календарь» — вкладка телефона «как приложение» и раздел меню V5 (владелец,
// 20.10: «вкладку как в приложении нужно»): карточка «Календарь и история
// тренировок — в приложении». Самого календаря на сайте нет; у кого вкладки нет
// (прежнее меню), адрес ведёт на «Тренировки» (решает сама страница).
const CalendarAppPage = lazy(() =>
    import("@/pages/AppOnlySectionPage").then((m) => ({
        default: m.CalendarAppPage,
    })),
);
const CommunityGroupCreatPage = lazy(() =>
    import("@/pages/CommunityGroupCreatPage").then((m) => ({
        default: m.CommunityGroupCreatPage,
    })),
);
// Новые экраны телефонного кабинета «как приложение» (features/appShell,
// предпросмотр для админов). Этих адресов раньше не было: всем, кто
// оболочку не видит, они отвечают тем же голым «404» (isAppOnlyPath).
const ProfileMenuPage = lazy(() =>
    import("@/pages/ProfileMenuPage").then((m) => ({ default: m.ProfileMenuPage })),
);
// «Все встречи» вкладки «Общение и встречи» в оболочке (ActivitiesListScreen приложения).
const CommunityMeetingsApp = lazy(() =>
    import("@/pages/CommunityMapPage/ui/CommunityMeetingsApp").then((m) => ({ default: m.CommunityMeetingsApp })),
);
const CommunityMapFullPage = lazy(() =>
    import("@/pages/CommunityMapFullPage").then((m) => ({ default: m.CommunityMapFullPage })),
);
const GroupInfoPage = lazy(() =>
    import("@/pages/GroupInfoPage").then((m) => ({ default: m.GroupInfoPage })),
);
const GroupsDiscoverPage = lazy(() =>
    import("@/pages/GroupsDiscoverPage").then((m) => ({ default: m.GroupsDiscoverPage })),
);
const GroupCreatePage = lazy(() =>
    import("@/pages/GroupCreatePage").then((m) => ({ default: m.GroupCreatePage })),
);
const CommunityModerationPage = lazy(() =>
    import("@/pages/CommunityModerationPage").then((m) => ({ default: m.CommunityModerationPage })),
);
const FriendsPage = lazy(() =>
    import("@/pages/FriendsPage").then((m) => ({ default: m.FriendsPage })),
);
const PeopleSearchPage = lazy(() =>
    import("@/pages/PeopleSearchPage").then((m) => ({ default: m.PeopleSearchPage })),
);
const PersonProfilePage = lazy(() =>
    import("@/pages/PersonProfilePage").then((m) => ({ default: m.PersonProfilePage })),
);
// Профиль v2 (PROFILE_CONTRACT §5.2): своя карточка и «Как меня видят». Кому не открыт —
// страницы сами уводят на прежнюю /account/profile.
const MyProfilePage = lazy(() =>
    import("@/pages/MyProfilePage").then((m) => ({ default: m.MyProfilePage })),
);
const MyProfilePreviewPage = lazy(() =>
    import("@/pages/MyProfilePage").then((m) => ({ default: m.MyProfilePreviewPage })),
);
// «О себе и интересы» (профиль v2.1): текст, метки, кто видит.
const MyAboutPage = lazy(() =>
    import("@/pages/MyProfilePage").then((m) => ({ default: m.MyAboutPage })),
);
const CreateActivityPage = lazy(() =>
    import("@/pages/CreateActivityPage").then((m) => ({ default: m.CreateActivityPage })),
);
const ClubPage = lazy(() =>
    import("@/pages/ClubPage").then((m) => ({ default: m.ClubPage })),
);
const BlagostClubsPage = lazy(() =>
    import("@/pages/BlagostClubsPage").then((m) => ({
        default: m.BlagostClubsPage,
    })),
);
const SupportPage = lazy(() =>
    import("@/pages/SupportPage").then((m) => ({
        default: m.SupportPage,
    })),
);

const PersonalMeetOgtPage = lazy(() =>
    import("@/pages/PersonalMeetOgtPage").then((m) => ({
        default: m.PersonalMeetOgtPage,
    })),
);
const AiSearchPage = lazy(() =>
    import("@/pages/AiSearchPage").then((m) => ({
        default: m.AiSearchPage,
    })),
);
// Loading fallback component
const PageLoader = () => (
    <div
        style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            background: "#040404",
        }}
    >
        <div
            style={{
                width: 40,
                height: 40,
                border: "3px solid rgba(255,255,255,0.2)",
                borderTopColor: "#fff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
            }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
);

// Wrap lazy components with Suspense
const withSuspense = (
    Component: React.LazyExoticComponent<React.ComponentType>,
) => (
    <Suspense fallback={<PageLoader />}>
        <Component />
    </Suspense>
);

export const routeConfig: Record<AppRoutes, AppRoutesProps> = {
    [AppRoutes.LANDING]: {
        path: "landing",
        element: withSuspense(MainPage),
        authOnly: false,
    },
    // Скрытые старые страницы: адрес ведёт на главную языка (ToLangHome выше).
    // Главная открыта всем, поэтому и /main больше не просит входа.
    [AppRoutes.MAINRU]: {
        path: AppRoutes.MAINRU,
        element: <ToLangHome />,
        authOnly: false,
    },
    [AppRoutes.MAIN]: {
        path: "main",
        element: <ToLangHome />,
        authOnly: false,
    },
    [AppRoutes.WATCH_LINK]: {
        path: "watch-link",
        element: withSuspense(WatchLinkPage),
        authOnly: false,
    },
    [AppRoutes.WATCH_LINK_DONE]: {
        path: "watch-link/done",
        element: withSuspense(WatchLinkPage),
        authOnly: false,
    },
    [AppRoutes.EDITOR_LINK]: {
        path: "editor-link",
        element: withSuspense(EditorLinkPage),
        authOnly: false,
    },
    [AppRoutes.TermsAndConditions]: {
        path: AppRoutes.TermsAndConditions,
        element: withSuspense(TermsAndConditionsPage),
        authOnly: true,
    },
    [AppRoutes.PLAYER]: {
        path: "player",
        element: <Navigate to="3de70c09848148c58d15b788beb843e3" replace />,
        authOnly: true,
    },
    [AppRoutes.PLAYER_MEDIA]: {
        path: "player/:mediaId",
        element: withSuspense(Player),
        authOnly: true,
    },
    // `/{lang}/call/{mediaId}` -- the 1:1, standing on its own. Nothing here
    // plays HLS, so there is no second clock to confuse with the conversation
    // and no surface to swap. `authOnly`: the participant identity comes from
    // the signed-in account, exactly as on the watch page.
    [AppRoutes.LIVE_CALL]: {
        path: "call/:mediaId",
        element: withSuspense(LiveCallPage),
        authOnly: true,
    },
    // `/{lang}/admin/stream` -- the OTHER half of the live console. The
    // conversation console answers "who is in the room"; this answers "is the
    // lecture coming out the other end", which is a different job with different
    // failure modes, and mixing them made both harder to read.
    [AppRoutes.ADMIN_STREAM]: {
        path: "admin/stream",
        element: withSuspense(AdminStreamPage),
        authOnly: true,
    },
    // `/{lang}/devicecheck/{mediaId}` -- what a phone actually does, measured on
    // the phone. Every device fault here has looked from the server exactly like
    // somebody who was not speaking or not moving, so the device answers for
    // itself. `authOnly` because it joins the room for real, as that account.
    [AppRoutes.DEVICE_CHECK]: {
        path: "devicecheck/:mediaId",
        element: withSuspense(DeviceCheckPage),
        authOnly: true,
    },
    [AppRoutes.LOGIN]: {
        path: "login",
        element: withSuspense(LoginPage),
    },
    [AppRoutes.GOOGLE_CALLBACK]: {
        path: "auth/google/callback",
        element: withSuspense(GoogleCallbackPage),
    },
    [AppRoutes.CALCULATOR]: {
        path: "calc",
        element: withSuspense(CalcPage),
    },
    [AppRoutes.RAG]: {
        path: "rag",
        element: withSuspense(ProtectedContent),
        authOnly: true,
    },
    [AppRoutes.ACCOUNT]: {
        path: "account",
        element: withSuspense(AccountPage),
        authOnly: true,
        children: [
            {
                index: true,
                element: withSuspense(HomeSection),
            },
            {
                path: "consultations",
                element: withSuspense(ConsultationsSection),
            },
            {
                path: "consultations-email",
                element: withSuspense(ConsultationsProfileSection),
            },
            {
                path: "diet",
                element: withSuspense(DietPage),
            },
            {
                path: "diagnose",
                element: withSuspense(Diagnose),
            },
            {
                path: "trees",
                element: withSuspense(TreesPage),
            },
            {
                path: "area_of_life",
                element: withSuspense(AreaOfLifePage),
            },
            {
                path: "lectures",
                element: withSuspense(LecturesPage),
            },
            {
                // «Эфиры» (бывшие эфиры «Моих лекций») — пока только
                // администраторам: остальным StreamsRoute открывает «Мои лекции».
                path: "streams",
                element: <StreamsRoute />,
            },
            {
                path: "answers",
                element: withSuspense(AnswersPage),
            },
            {
                // Тестовая тренировка: время начала, окончания и два снимка.
                // Плана не требует — надбавка за неё от плана не зависит.
                //
                // Вносят её в приложении (владелец, 07.10), поэтому адрес
                // сайта ведёт на «Тренировки» — той же калиткой, что календарь,
                // план и загрузку файла. Страница цела: откат рубильника
                // SITE_WORKOUT_PAGES_HIDDEN возвращает её одним словом.
                path: "testing-workout",
                element: <SiteWorkoutPageGate page="test">{withSuspense(TestingWorkoutPage)}</SiteWorkoutPageGate>,
            },
            {
                path: "questions",
                element: withSuspense(QuestionsPage),
            },
            {
                // Календарь, план и загрузка файла — только в приложении
                // (SITE_WORKOUT_PAGES_HIDDEN): кому скрыты, тому адрес
                // открывает «Тренировки» — карточку приложения.
                path: "workouts",
                element: <SiteWorkoutPageGate page="calendar">{withSuspense(WorkoutsPage)}</SiteWorkoutPageGate>,
            },
            {
                // «Тренировки» сайта без приложения — карточка приложения и
                // тестовая тренировка.
                path: "training",
                element: withSuspense(TrainingAppPage),
            },
            {
                // «Календарь» нижней панели «как в приложении» и меню V5
                // (владелец, 20.10): карточка приложения со своим заголовком.
                // Самого календаря на сайте нет (владелец, 03.10 и 09.10); у
                // кого вкладки нет (прежнее меню), адрес ведёт на «Тренировки».
                path: "calendar",
                element: withSuspense(CalendarAppPage),
            },
            {
                // «Аналитика и баллы»: бывшие вкладки «Прогресс» и «Баллы»
                // страницы тренировок. ?tab=points открывает баллы.
                path: "analytics",
                element: withSuspense(AnalyticsPage),
            },
            {
                // «История баллов»: все недели с первой тренировки, ?week=YYYY-Www —
                // неделя (план, тренировки, почему столько баллов). Кому видна —
                // POINTS_HISTORY_FOR (shared/config/pointsHistory.ts); остальных
                // страница отправляет на «Баллы».
                path: "points-history",
                element: withSuspense(PointsHistoryPage),
            },
            {
                // «Зоны пульса и длина шага» (?tab=hr|stride). Сюда ведёт
                // «посмотреть» на всех часах и в приложениях — с одноразовым
                // кодом входа &code=, который WebHandoffGate гасит до отрисовки
                // (с source=app код принимается на любой странице).
                path: "training-basis",
                element: withSuspense(TrainingBasisPage),
            },
            {
                path: "reviews",
                element: withSuspense(ReviewsSection),
            },
            { path: "specialist-chat", element: withSuspense(SpecialistChatPage) },
            { path: "knowledge", element: withSuspense(KnowledgePage) },
            {
                // Redirects to "community": one map of gatherings, not two. Kept
                // registered so old links and the app's deep links still land.
                path: "activities",
                element: withSuspense(AccountActivitiesMap),
            },
            {
                // Бывший календарь встреч: теперь это редирект на общий календарь
                // /account/workouts. Адрес оставлен — на него ведут старые ссылки,
                // туры и глубокие ссылки приложения; ?d/?v/?id сохраняются.
                // Пока календарь скрыт (SITE_WORKOUT_PAGES_HIDDEN), калитка
                // /account/workouts уводит дальше — на «Тренировки».
                path: "activities-calendar",
                element: <ToWorkoutsCalendar />,
            },
            {
                path: "good-deeds",
                element: withSuspense(GoodDeedsPage),
            },
            // Обе страницы вызываются из группы «Мои тренировки» бокового меню,
            // значит и рисоваться должны внутри кабинета. Плоскими маршрутами
            // они открывались на голом белом листе — ни меню, ни фона, ни
            // дороги назад.
            {
                path: "fit-upload",
                element: <SiteWorkoutPageGate page="upload">{withSuspense(FitUploadPage)}</SiteWorkoutPageGate>,
            },
            {
                path: "plan-create",
                element: <SiteWorkoutPageGate page="plan">{withSuspense(PlanCreatePage)}</SiteWorkoutPageGate>,
            },
            {
                // Страница одной встречи — в каркасе кабинета (боковое меню,
                // тема). Раньше была плоским маршрутом и выглядела чужой.
                path: "activity/:id",
                element: withSuspense(ActivityPage),
            },
            {
                path: "my-courses",
                element: withSuspense(MyCoursesPage),
            },
            {
                path: "my-courses-new",
                element: withSuspense(MyCoursesPageNew),
            },
            // Must precede the `:courseId` route below: "group" would
            // otherwise be swallowed as a course id.
            {
                path: "my-courses-new/group/:groupId",
                element: withSuspense(MyCoursesGroupPage),
            },
            {
                path: "my-courses-new/:courseId",
                element: withSuspense(MyCoursesCoursePage),
            },
            {
                path: "my-courses-new/:courseId/:lessonId",
                element: withSuspense(MyCoursesLessonPage),
            },
            {
                path: "settings",
                element: withSuspense(NotificationSettingsPage),
            },
            {
                path: "profile",
                element: withSuspense(ProfilePage),
            },
            {
                // «Мой профиль» — карточка профиля v2; «Изменить профиль» на ней — /profile выше.
                path: "me",
                element: withSuspense(MyProfilePage),
            },
            {
                // «Как меня видят»: ?as=all|friend|league.
                path: "me/preview",
                element: withSuspense(MyProfilePreviewPage),
            },
            {
                // «О себе и интересы» — из своего профиля и из анкеты.
                path: "me/about",
                element: withSuspense(MyAboutPage),
            },
            {
                path: "notifications",
                element: withSuspense(NotificationsPage),
            },
            {
                // Ссылка из пуша о приглашении на тренировку. Своего экрана у
                // приглашения нет: карточка с «Иду / Не могу» стоит в ленте
                // уведомлений, туда и ведём.
                path: "workout-invite/:inviteId",
                element: <ToAccount to="notifications" />,
            },
            {
                // Вкладка открывает карту мира: клубы и встречи. Чаты никуда не
                // делись — на них ведёт заметная ссылка со страницы и
                // отдельный адрес ниже, чтобы уже разосланные ссылки жили.
                path: "community",
                element: withSuspense(CommunityHomeGate),
            },
            {
                path: "community/chats",
                element: withSuspense(CommunityPage),
            },
            {
                // The gathering "Open the chat" and any group deep-link land on the
                // full community chat page (with the compose bar), not the old
                // landing card. CommunityPage reads :groupId to pre-select the group.
                path: "community/group/:groupId",
                element: withSuspense(CommunityPage),
            },
            {
                path: "community/group_create",
                element: withSuspense(CommunityGroupCreatPage),
            },
            // Телефонный кабинет «как приложение» (features/appShell): меню
            // профиля и экраны «Общения и встреч», которых в кабинете не было.
            { path: "menu", element: withSuspense(ProfileMenuPage) },
            { path: "community/map", element: withSuspense(CommunityMapFullPage) },
            { path: "community/meetings", element: withSuspense(CommunityMeetingsApp) },
            { path: "community/group/:groupId/info", element: withSuspense(GroupInfoPage) },
            { path: "community/groups", element: withSuspense(GroupsDiscoverPage) },
            { path: "community/groups/new", element: withSuspense(GroupCreatePage) },
            { path: "community/moderation", element: withSuspense(CommunityModerationPage) },
            {
                // Люди — вторая и третья двери под картой. Живут внутри
                // community/*, чтобы боковое меню оставалось раскрытым на них.
                path: "community/friends",
                element: withSuspense(FriendsPage),
            },
            {
                path: "community/people",
                element: withSuspense(PeopleSearchPage),
            },
            {
                path: "community/person/:personId",
                element: withSuspense(PersonProfilePage),
            },
            {
                // Создание встречи — своя страница, как CreateActivityScreen в
                // приложении; ?date=YYYY-MM-DD приходит из календаря.
                path: "community/create",
                element: withSuspense(CreateActivityPage),
            },
            {
                // Клуб — как ClubScreen приложения; в меню его нет, туда ведёт
                // строка «Клуб» на странице профиля.
                path: "community/club",
                element: withSuspense(ClubPage),
            },
            {
                // Адрес из уведомлений воркера о заявке в клуб (?tab=requests&club=…):
                // с 03.11 — в заявки кабинета клуба, иначе на страницу клуба.
                path: "club",
                element: <ToClubCabinet />,
            },
            {
                path: "affiliate",
                element: withSuspense(AffiliatePage),
            },
        ],
    },
    [AppRoutes.COURSE]: {
        path: "course",
        element: withSuspense(CoursePage),
        authOnly: false,
    },
    [AppRoutes.BUCKET_FILES]: {
        path: "bucket-files",
        element: withSuspense(BucketFilesPage),
    },
    [AppRoutes.YOUTUBE_VIDEOS]: {
        path: "youtube-videos",
        element: withSuspense(YouTubeVideosPage),
        authOnly: false,
    },
    [AppRoutes.WORKOUT_REVIEW]: {
        path: "workout-review",
        element: withSuspense(WorkoutReviewPage),
        authOnly: false,
    },
    [AppRoutes.ACTIVITY_REVIEW]: {
        path: "activity-review",
        element: withSuspense(ActivityReviewPage),
        authOnly: false,
    },
    [AppRoutes.LEAGUE]: {
        path: "league",
        element: withSuspense(LeaguePage),
        authOnly: false,
    },
    // «Лига участников» (доски LIGA_*): пока только для администраторов.
    // Плоский маршрут ВНЕ кабинета и без authOnly — намеренно: не вошедшему и
    // не администратору (и администратору в режиме «как обычный пользователь»)
    // адрес отвечает тем же голым «404», что и до страницы, без ухода на вход
    // и без каркаса кабинета. Администратору LeagueUsersRoute сам рисует
    // каркас кабинета (AccountPage) со страницей лиги внутри; сервер отвечает
    // 403 всем остальным. Хвост адреса — экраны лиги: me, person/:id (профиль
    // участника; прежний compare/:id ведёт туда же), marathons[/:id].
    [AppRoutes.LEAGUE_USERS]: {
        path: "account/league-users/*",
        element: <LeagueUsersRoute />,
        authOnly: false,
    },
    // v5-products: «Все продукты» (доски PROD_1, PROD_2) — пока только администраторам.
    // Тот же приём, что у LEAGUE_USERS: плоский маршрут вне кабинета и без authOnly,
    // чтобы всем остальным (и админу «как обычный пользователь») адрес отвечал тем же
    // голым «404», что и до страницы. Администратору ProductsRoute сам рисует каркас
    // кабинета со страницей внутри. Страница только читает — ничего не продаёт.
    [AppRoutes.PRODUCTS]: {
        path: "account/products/*",
        element: <ProductsRoute />,
        authOnly: false,
    },
    // «Достижения» (своя страница: ближайшие, уровни, все достижения по сферам) —
    // пока предпросмотр для администраторов. Тот же приём, что у LEAGUE_USERS:
    // плоский маршрут вне кабинета и без authOnly — всем остальным (и админу «как
    // обычный пользователь») адрес отвечает тем же голым «404», что и до страницы.
    // Администратору AchievementsRoute сам рисует каркас кабинета со страницей.
    [AppRoutes.ACHIEVEMENTS]: {
        path: "account/achievements/*",
        element: <AchievementsRoute />,
        authOnly: false,
    },
    [AppRoutes.ADMIN_MARATHONS]: {
        path: "admin-marathons",
        element: withSuspense(AdminMarathonsPage),
        authOnly: false,
    },
    // Админка марафонов лиги: список, форма, «Вручения», менеджеры, уровни,
    // призы. Хвост адреса — экраны пульта (new, <id>). Вход обязателен, права
    // проверяет страница (/manage/me) и каждый маршрут site_server.
    [AppRoutes.ADMIN_LEAGUE_MARATHONS]: {
        path: "admin/marathons/*",
        element: withSuspense(AdminLeagueMarathonsPage),
        authOnly: true,
    },
    // Гейт внутри страницы (useIsAdmin), как и у соседних админских: authOnly
    // проверяет только вход, а не права, и настоящая защита всё равно на
    // воркере — /admin/geo/members отвечает 403 всем, кроме администраторов.
    // Сама страница живёт в кабинете (см. детей ACCOUNT); здесь остаётся
    // только старый адрес, чтобы уже разосланные ссылки открывались.
    [AppRoutes.PLAN_CREATE]: {
        path: "plan-create",
        element: <ToAccount to="plan-create" />,
    },
    [AppRoutes.ADMIN_CLUBS_RADIUS]: {
        path: "admin-clubs-radius",
        element: withSuspense(AdminClubsRadiusPage),
        // Вход обязателен, а права проверяет сама страница (useIsAdmin) и
        // воркер (403). Три слоя, потому что первый уводит анонимного на вход,
        // второй не рисует страницу постороннему, а третий не отдаёт данные.
        authOnly: true,
    },
    [AppRoutes.CLUB_MANAGER]: {
        path: "club-manager/*",
        element: withSuspense(ClubManagerPage),
        authOnly: false,
    },
    // Ссылка-приглашение в клуб (кабинет клуба, 03.11): /:lang/club-invite/<код>. Публичная —
    // карточку клуба видно без входа, вступить — после входа (страница сама зовёт войти и
    // возвращает сюда; код public/index.html убирает из адреса до счётчика, как у подарка).
    [AppRoutes.CLUB_INVITE]: {
        path: "club-invite/:code?",
        element: withSuspense(ClubJoinPage),
        authOnly: false,
    },
    // Клубы для организаторов движения: заявки «Я веду этот клуб» и передачи клуба, предложения
    // открыть клуб, заявки в клубы без руководителя (сюда же ведёт уведомление club_proposed).
    // Вход обязателен, права проверяет сама страница (useIsAdmin) и воркер (403).
    [AppRoutes.ADMIN_CLUBS]: {
        path: "admin/clubs",
        element: withSuspense(AdminClubsPage),
        authOnly: true,
    },
    [AppRoutes.HEALTHE_GUIDE]: {
        path: AppRoutes.HEALTHE_GUIDE,
        element: withSuspense(HealthGuideNewPage),
        authOnly: false,
    },
    [AppRoutes.VIDEO_COURSE]: {
        path: AppRoutes.VIDEO_COURSE,
        element: withSuspense(VideoCoursePage),
        authOnly: false,
    },
    [AppRoutes.ANSWER]: {
        path: "answer",
        element: withSuspense(AnswerPage),
        authOnly: true,
    },
    [AppRoutes.ACTIVITIES_MAP]: {
        path: "activities-map",
        element: withSuspense(ActivitiesMapPage),
        authOnly: false,
    },
    [AppRoutes.PRIVACY_POLICY]: {
        path: "privacy-policy",
        element: withSuspense(PrivacyPolicyPage),
        authOnly: false,
    },
    // Как и с планом: страница переехала в кабинет, адрес остался.
    [AppRoutes.FIT_UPLOAD]: {
        path: "fit-upload",
        element: <ToAccount to="fit-upload" />,
    },
    [AppRoutes.SCORING_ANALYSIS]: {
        path: "scoring-analysis",
        element: withSuspense(ScoringAnalysisPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_DASHBOARD]: {
        path: "admin",
        element: withSuspense(AdminDashboardPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_USER_PROFILE]: {
        path: "admin/users/:email",
        element: withSuspense(AdminUserProfilePage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_USER_PROFILE_BY_UID]: {
        path: "admin/users/uid/:uid",
        element: withSuspense(AdminUserProfilePage),
        authOnly: true,
    },
    [AppRoutes.INDIA_VIDEO_ANSWER]: {
        path: "india-video-answer",
        element: withSuspense(IndiaVideoAnswerPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_DIRECT_ANSWER]: {
        path: "admin-direct-answer",
        element: withSuspense(AdminDirectAnswerPage),
        // Намеренно НЕ authOnly. У страницы две двери: админская сессия сайта
        // и вход по паре «e-mail + пароль». RequireAuth смотрит только на
        // объект user в localStorage, которого при входе по паролю нет, и
        // перебрасывал на /ru/login ДО того, как страница успевала показать
        // свою форму — то есть вторая дверь была недостижима.
        // Пускать сюда анонима безопасно: страница сама показывает форму входа,
        // а каждый эндпоинт на сервере требует подписанный токен и проверяет
        // адрес по direct_answer_access.is_allowed.
    },
    [AppRoutes.ADMIN_CLIP_INSERTS]: {
        path: "admin-clip-inserts",
        element: withSuspense(AdminClipInsertsPage),
        authOnly: true,
    },
    [AppRoutes.LECTURES]: {
        path: "account/lectures",
        element: withSuspense(LecturesPage),
        authOnly: true,
    },
    [AppRoutes.DIAGNOSTIC_QUESTIONNAIRE]: {
        path: "account/questionnaire",
        element: withSuspense(DiagnosticQuestionnairePage),
        authOnly: true,
    },
    [AppRoutes.ORDER_AFTER_PAY]: {
        path: "account/health-details",
        element: withSuspense(OrderAfterPayPage),
        authOnly: true,
    },
    [AppRoutes.PAYMENT_SUCCESS]: {
        path: "account/payment-success",
        element: withSuspense(PaymentSuccessPage),
        authOnly: true,
    },
    [AppRoutes.REVIEWS]: {
        path: "account/reviews",
        element: withSuspense(ReviewsSection),
        authOnly: true,
    },
    [AppRoutes.ACCOUNT_ACTIVITIES]: {
        path: "account/activities",
        element: withSuspense(AccountActivitiesMap),
        authOnly: true,
    },
    // Страница встречи живёт среди детей ACCOUNT (см. выше) и рисуется внутри
    // каркаса кабинета. Ключ обязателен для Record<AppRoutes, …>, поэтому здесь
    // короткий адрес /{lang}/activity/:id ведёт в кабинет.
    [AppRoutes.ACTIVITY_DETAIL]: {
        path: "activity/:id",
        element: <ToAccountActivity />,
    },
    // AppRoutes.ACTIVITIES_CALENDAR is now an ACCOUNT child (see above) so it renders
    // inside the account layout. Registering it here as well would match twice.
    [AppRoutes.ADMIN_GEO_DENSITY]: {
        path: "admin/geo-density",
        element: withSuspense(AdminGeoDensityPage),
        authOnly: true,
    },
    [AppRoutes.MY_REVIVE]: {
        path: AppRoutes.MY_REVIVE,
        element: withSuspense(MyReviewPage),
    },
    [AppRoutes.QUESTION_DATABASE]: {
        path: "question-database",
        element: withSuspense(AdminQuestionDatabasePage),
        authOnly: true,
    },
    [AppRoutes.USER_QUESTIONS]: {
        path: "user-questions",
        element: withSuspense(AdminUserQuestionsPage),
        authOnly: true,
    },
    [AppRoutes.USER_QUESTION_DETAIL]: {
        path: "user-questions/:id",
        element: withSuspense(AdminUserQuestionDetailPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_REJECT_REASONS]: {
        path: "admin/reject-reasons",
        element: withSuspense(AdminRejectReasonsPage),
        authOnly: true,
    },
    [AppRoutes.ANSWER_CORRECTIONS]: {
        path: "answer-corrections",
        element: withSuspense(AdminAnswerCorrectionsPage),
        authOnly: true,
    },
    [AppRoutes.ANSWER_CORRECTION_EDITOR]: {
        path: "answer-corrections/:uuid",
        element: withSuspense(AdminAnswerCorrectionEditorPage),
        authOnly: true,
    },
    [AppRoutes.SUBTITLE_CORRECTIONS]: {
        path: "subtitle-corrections",
        element: withSuspense(AdminCorrectionsPage),
        authOnly: true,
    },
    [AppRoutes.SUBTITLE_CHANGELOG]: {
        path: "subtitle-changelog",
        element: withSuspense(AdminChangelogPage),
        authOnly: true,
    },
    // Словарь переводов (проект словаря §5.1). Три слоя: вход (authOnly) → роль на странице
    // (GET /glossary/me: админ сайта или переводчик по whois) → 403 на site_server.
    [AppRoutes.GLOSSARY]: {
        path: "glossary",
        element: withSuspense(AdminGlossaryPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_CRASH_REPORTS]: {
        path: "admin-crash-reports",
        element: withSuspense(AdminCrashReportsPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_MODERATION]: {
        path: "admin-moderation",
        element: withSuspense(AdminModerationPage),
        authOnly: true,
    },
    // Инбокс вопросов по курсам: что ещё ждёт ответа специалиста.
    // Отвечать отсюда нельзя — ответы приходят только из CRM.
    [AppRoutes.ADMIN_COURSE_QA]: {
        path: "admin/course-qa",
        element: withSuspense(AdminCourseQaPage),
        authOnly: true,
    },
    // «Нашли ошибку?» — сообщения со всех страниц и из рецептов: принять (баллы автору) или отклонить.
    // Вход обязателен, права проверяет сама страница (useIsAdmin), данные отдаёт только site_server
    // под require_admin — те же три слоя, что у соседних админских.
    [AppRoutes.ADMIN_ERROR_REPORTS]: {
        path: "admin/error-reports",
        element: withSuspense(AdminErrorReportsPage),
        authOnly: true,
    },
    // Модерация сообщества для сотрудников (модераторы + админы); права — у воркера.
    [AppRoutes.ADMIN_COMMUNITY]: {
        path: "admin-community",
        element: withSuspense(AdminCommunityPage),
        authOnly: true,
    },
    [AppRoutes.ACCOUNT_SETTINGS]: {
        path: "account/settings",
        element: withSuspense(NotificationSettingsPage),
        authOnly: true,
    },
    [AppRoutes.ACCOUNT_PROFILE]: {
        path: "account/profile",
        element: withSuspense(ProfilePage),
        authOnly: true,
    },
    [AppRoutes.ACCOUNT_NOTIFICATIONS]: {
        path: "account/notifications",
        element: withSuspense(NotificationsPage),
        authOnly: true,
    },
    [AppRoutes.ACCOUNT_SPECIALIST_CHAT]: {
        path: "account/specialist-chat", element: withSuspense(SpecialistChatPage), authOnly: true,
    },
    [AppRoutes.ACCOUNT_KNOWLEDGE]: {
        path: "account/knowledge", element: withSuspense(KnowledgePage), authOnly: true,
    },
    [AppRoutes.TELEGRAM_ACCESS]: {
        path: "telegram-access", element: withSuspense(TelegramAccessPage), authOnly: false,
    },
    [AppRoutes.HEALTH_METHOD_COURSE]: {
        path: AppRoutes.HEALTH_METHOD_COURSE,
        // Старый HealthMethodCoursePage пока оставлен в коде, но на этом
        // маршруте теперь отдаётся VideoCoursePage.
        element: withSuspense(VideoCoursePage),
        authOnly: false,
    },
    [AppRoutes.PRIVACY_POLICY_PUBLIC]: {
        path: "privacy_policy",
        element: withSuspense(PrivacyPolicyPublicPage),
        authOnly: false,
    },
    [AppRoutes.DELETE_ACCOUNT]: {
        path: "delete-account",
        element: withSuspense(DeleteAccountPage),
        authOnly: false,
    },
    [AppRoutes.PRIVACY_ALIAS]: {
        // Short alias for /privacy_policy — external links (signup banners,
        // emails, support replies) use /<lang>/privacy; keep them resolving
        // instead of returning 404.
        path: "privacy",
        element: withSuspense(PrivacyPolicyPublicPage),
        authOnly: false,
    },
    [AppRoutes.TERMS_ALIAS]: {
        // Short, public alias for the terms-of-service page — resolves the
        // legal disclaimer link on the signup form (which previously 404'd).
        path: "terms",
        element: withSuspense(TermsAndConditionsPage),
        authOnly: false,
    },
    [AppRoutes.CANCELLATION_POLICY]: {
        path: "cancellation_policy",
        element: withSuspense(CancellationPolicyPage),
        authOnly: false,
    },
    [AppRoutes.TRANSLATED_LECTURES]: {
        path: "translated-lectures",
        element: withSuspense(TranslatedLecturesPage),
        authOnly: false,
    },
    [AppRoutes.OGT_MAIN_PAGE]: {
        path: "ogt-main-page",
        element: withSuspense(OgtMainPage),
        authOnly: false,
    },
    [AppRoutes.PERSONAL_CONSULTATION]: {
        path: "personal-consultation",
        element: withSuspense(PersonalMeetOgtPage),
        authOnly: false,
    },
    [AppRoutes.SUPPORT]: {
        path: "support",
        element: withSuspense(SupportPage),
        authOnly: false,
    },
    [AppRoutes.ABOUT_OGT]: {
        path: "about-ogt",
        element: withSuspense(AboutOgtPage),
        authOnly: false,
    },
    [AppRoutes.VERIFY_EMAIL]: {
        path: "verify-email",
        element: withSuspense(VerifyEmailPage),
        authOnly: false,
    },
    [AppRoutes.CONFIRM_PURCHASE]: {
        path: "confirm-purchase",
        element: withSuspense(ConfirmPurchasePage),
        authOnly: false,
    },
    [AppRoutes.SET_PASSWORD]: {
        path: "set-password",
        element: withSuspense(SetPasswordPage),
        authOnly: false,
    },
    [AppRoutes.QUESTIONS_PUBLIC]: {
        path: "questions",
        element: withSuspense(PublicQuestionsPage),
        authOnly: false,
    },
    [AppRoutes.CUT_VIDEOS]: {
        path: "cut-videos",
        element: withSuspense(CutVideosPage),
        authOnly: false,
    },
    [AppRoutes.CUT_VIDEO]: {
        path: "cut-videos/:cutId",
        element: withSuspense(CutVideosPage),
        authOnly: false,
    },
    // Разбор реза до его применения. Гейт — внутри страницы (useIsAdmin), как у
    // /admin/coupons: authOnly здесь только увёл бы гостя на логин вместо внятного
    // «только для администраторов», а сам рез всё равно требует session_token на сервере.
    [AppRoutes.CLIP_CUTS]: {
        path: "admin/clip-cuts",
        element: withSuspense(ClipCutsPage),
        authOnly: false,
    },
    [AppRoutes.CLIP_CUT_ONE]: {
        path: "admin/clip-cuts/:cutId",
        element: withSuspense(ClipCutsPage),
        authOnly: false,
    },
    // Предпросмотр склеек «лекция + курс». Видео остаётся на Google Drive и на сайт
    // НЕ заливается — страница показывает встроенный плеер Диска. Гейт, как у
    // /admin/clip-cuts: useIsAdmin внутри страницы.
    [AppRoutes.COURSE_CUTS]: {
        path: "admin/course-cuts",
        element: withSuspense(CourseCutsPage),
        authOnly: false,
    },
    [AppRoutes.CUT_PREVIEW]: {
        path: "cut-preview",
        element: withSuspense(CutPreviewPage),
        authOnly: false,
    },
    [AppRoutes.CUT_PREVIEW_ONE]: {
        path: "cut-preview/:cutId",
        element: withSuspense(CutPreviewPage),
        authOnly: false,
    },
    [AppRoutes.MY_COURSES_NEW]: {
        path: "account/my-courses-new",
        element: withSuspense(MyCoursesPageNew),
        authOnly: true,
    },
    [AppRoutes.MY_COURSES_NEW_GROUP]: {
        path: "account/my-courses-new/group/:groupId",
        element: withSuspense(MyCoursesGroupPage),
        authOnly: true,
    },
    [AppRoutes.MY_COURSES_NEW_DETAIL]: {
        path: "account/my-courses-new/:courseId",
        element: withSuspense(MyCoursesCoursePage),
        authOnly: true,
    },
    [AppRoutes.MY_COURSES_NEW_LESSON]: {
        path: "account/my-courses-new/:courseId/:lessonId",
        element: withSuspense(MyCoursesLessonPage),
        authOnly: true,
    },
    [AppRoutes.BUY_COURSE]: {
        // Public, shareable Stripe-backed payment landing — /:lang/buy/:courseId.
        path: "buy/:courseId",
        element: withSuspense(BuyCoursePage),
        authOnly: false,
    },
    [AppRoutes.BUY_SUBSCRIPTION]: {
        // Public, shareable Stripe-backed subscription purchase — /:lang/buy-subscription.
        path: "buy-subscription",
        element: withSuspense(BuySubscriptionPage),
        authOnly: false,
    },
    [AppRoutes.MANUAL_HUB]: {
        // Public user manual hub — /:lang/manual.
        path: "manual",
        element: withSuspense(ManualHubPage),
        authOnly: false,
    },
    [AppRoutes.MANUAL]: {
        // Per-app-version manual — /:lang/manual/:platform.
        path: "manual/:platform",
        element: withSuspense(ManualPage),
        authOnly: false,
    },
    [AppRoutes.PACKAGES]: {
        path: "packages",
        element: withSuspense(PackagesPage),
        authOnly: false,
    },
    // Страница получателя подарка — открыта всем, у кого есть ссылка: вход
    // просит сама страница и возвращает на неё же. Токен в адресе (:giftToken)
    // бывает, только если index.html его не забрал; страница тут же уходит на
    // /:lang/gift (features/gifts/lib/giftLink.ts).
    [AppRoutes.GIFT]: {
        path: "gift/:giftToken?",
        element: withSuspense(GiftClaimPage),
        authOnly: false,
    },
    // «Мои подарки» — пока только администраторам. Плоский маршрут без
    // authOnly, как LEAGUE_USERS: всем остальным адрес отвечает тем же голым
    // «404», что и до страницы (решает MyGiftsRoute).
    [AppRoutes.MY_GIFTS]: {
        path: "account/gifts/*",
        element: <MyGiftsRoute />,
        authOnly: false,
    },
    [AppRoutes.ADMIN_COURSE_PAYMENTS]: {
        // ADMIN_EMAILS gate inside the page; authOnly here just ensures
        // someone is logged in before we even check the email.
        path: "admin/course-payments",
        element: withSuspense(AdminCoursePaymentsPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_LIVE_STAGE]: {
        // ADMIN_EMAILS gate is enforced SERVER-SIDE by the live API, which
        // verifies the session JWT rather than trusting a header. authOnly here
        // only ensures someone is signed in so a token exists to send.
        path: "admin/live",
        element: withSuspense(AdminLiveStagePage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_SHOW]: {
        // «Кто прав» show console door. ADMIN_EMAILS gate inside the page (UX) and,
        // authoritatively, on the show server, which verifies the session JWT itself.
        path: "admin/show",
        element: withSuspense(AdminShowPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_COUPONS]: {
        // ADMIN_EMAILS gate inside the page (isAdmin from shared/config/admin).
        path: "admin/coupons",
        element: withSuspense(AdminCouponDistributionPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_SUBSCRIPTIONS]: {
        // ADMIN_EMAILS gate inside the page (isAdmin from shared/config/admin).
        path: "admin/subscriptions",
        element: withSuspense(AdminSubscriptionsPage),
        authOnly: true,
    },
    [AppRoutes.METHOD]: {
        // Витрина метода открыта всем: сама она — статическая страница
        // public/method/, которая и так раздаётся по прямому адресу.
        path: "method",
        element: withSuspense(MethodPage),
        authOnly: false,
    },
    [AppRoutes.GET_APP]: {
        // QR окна магазинов (умная кнопка на компьютере) открывает эту страницу на телефоне.
        path: "get-app",
        element: withSuspense(GetAppPage),
        authOnly: false,
    },
    [AppRoutes.ADMIN_GOOGLEPLAY_PROMOS]: {
        // ADMIN_EMAILS gate inside the page (isAdmin from shared/config/admin).
        path: "admin/googleplay-promos",
        element: withSuspense(AdminGooglePlayPromosPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_TESTING]: {
        // Worker-side gate via X-Admin-Email (COMMUNITY_ADMIN_EMAILS).
        // authOnly here just ensures someone is logged in.
        path: "admin/testing",
        element: withSuspense(AdminTestingPage),
        authOnly: true,
    },
    [AppRoutes.ADMIN_PERSONAL_CONSULTATION]: {
        // ADMIN_EMAILS gate inside the page (isAdmin from shared/config/admin).
        // Optional :id opens a submission's detail modal directly (used by
        // the Telegram notification link).
        path: "admin/personal-consultation/:id?",
        element: withSuspense(AdminPersonalConsultationPage),
        authOnly: true,
    },
    [AppRoutes.BLAGOST_CLUBS]: {
        path: "blagost-clubs",
        element: withSuspense(BlagostClubsPage),
        authOnly: false,
    },
    [AppRoutes.AI_SEARCH]: {
        path: "ai-search",
        element: withSuspense(AiSearchPage),
        authOnly: false,
    },
    [AppRoutes.NOT_FOUND]: {
        path: "*",
        element: <div>404</div>,
    },
};
